import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./AddMetaPluginModal.module.css";
import ModalActionBar from "shared/ModalActionBar";
import { Modal } from "shared/Modal";
import { usePlugins } from "DreamStream/PluginsPage/usePlugins";
import classNames from "classnames";
import { Ok } from "react-ikonate";

/**
 * Interactive component.
 * Modal used to add a meta plugin.
 */
export function AddMetaPluginModal({ isOpen, onClose, onClick, value }) {
  const { data, loading } = usePlugins();

  const [name, setName] = useState("");
  const [iboNumber, setIboNumber] = useState("");
  const [pinLevel, setPinLevel] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [newFilter, setNewFilter] = useState("");
  const [newTargetFilter, setNewTargetFilter] = useState("");
  const [openDropDown, setOpenDropdown] = useState(false);

  const [orgName, setOrgName] = useState("");
  const [orgOrganizationId, setOrgOrganizationId] = useState("");
  const [orgIboNumber, setOrgIboNumber] = useState("");
  const [orgPinLevel, setOrgPinLevel] = useState("");

  const [parentOrganization, setParentOrganization] = useState("");
  const [openGroupDropDown, setOpenGroupDropdown] = useState(false);
  const [errors, setErrors] = useState({ orgName: "", groupName: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [syncChecked, setSyncChecked] = useState(false);

  const [selectedPlugee, setSelectedPlugee] = useState("");
  const [selectedTarget, setSelectedTarget] = useState("");

  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setErrors({ orgName: "", groupName: "" });
      clearInput();
    }
  }, [isOpen]);

  const handleDropdownClick = () => {
    setOpenDropdown(!openDropDown);
    ref.current.focus();
  };

  const groupsData = data?.filter(
    (org) => org.groups.length > 0 && org.pinLevel !== 0
  );
  const sortedData = groupsData?.reduce((acc, org) => {
    org.groups.forEach((group) => {
      const newOrgEntry = { ...org, groups: [group] };
      acc.push(newOrgEntry);
    });
    return acc;
  }, []);

  function filteredData() {
    const newFilterTrimmed = newFilter.trim();

    const suggestGroup = sortedData.filter(
      (org) =>
        (org.groups &&
          org.groups.some((group) =>
            group.groupName
              .toLowerCase()
              .includes(newFilterTrimmed.trim().toLowerCase())
          )) ||
        (org.organizationName &&
          org.organizationName
            .toLowerCase()
            .includes(newFilterTrimmed.trim().toLowerCase())) ||
        (org.iboNumber &&
          String(org.iboNumber).includes(newFilterTrimmed.trim()))
    );

    return suggestGroup;
  }
  // Check if the group is exists in organization meta
  const orgExist = data?.reduce((acc, group) => {
    if (
      (group.pinLevel === parentOrganization.pinLevel ||
        (group.pinLevel === 0 &&
          group.groups &&
          group.groups.length > 0 &&
          group.organizationId)) &&
      !group.meta.some(
        (meta) => meta.iboNumber === parentOrganization.iboNumber
      ) &&
      group.iboNumber !== 0
    ) {
      if (group.groups && group.groups.length > 0) {
        group.groups.forEach((item) => {
          const newGroup = { ...group, groups: [item] };
          acc.push(newGroup);
        });
      } else {
        acc.push(group);
      }
    }
    return acc;
  }, []);

  // Filter Target Data
  function filteredTargetData(orgExist) {
    const newTargetFilterTrimmed = newTargetFilter.trim();

    const suggestGroup = orgExist.filter(
      (org) =>
        (org.groups &&
          org.groups.some((group) =>
            group.groupName
              .toLowerCase()
              .includes(newTargetFilterTrimmed.trim().toLowerCase())
          )) ||
        (org.organizationName &&
          org.organizationName
            .toLowerCase()
            .includes(newTargetFilterTrimmed.trim().toLowerCase())) ||
        (org.iboNumber &&
          String(org.iboNumber).includes(newTargetFilterTrimmed.trim()))
    );

    return suggestGroup;
  }

  function displayValue(isPrimary) {
    if (isPrimary) {
      if (newFilter.length > 0) {
        return newFilter;
      }
      if (value) {
        return `${orgName} (${orgIboNumber} )`;
      }
      return newFilter;
    } else if (!isPrimary) {
      if (newTargetFilter.length > 0) {
        return newTargetFilter;
      }
      if (value) {
        return `${orgName} (${orgIboNumber} )`;
      }
      return newTargetFilter;
    }
  }

  const groupSearchSelect = (group, isPrimary) => {
    const display = [
      group.iboNumber,
      group.organizationName,
      group.groups[0].groupName,
    ];

    if (isPrimary) {
      setSelectedPlugee(display);
      setParentOrganization(group);
      setNewFilter("");
      setGroupName("");
      setOpenGroupDropdown(false);
      setOpenDropdown(false);
      setSelectedTarget("");
    }

    if (!isPrimary) {
      setSelectedTarget(display);
      setName(group.name);
      setIboNumber(group.iboNumber);
      setPinLevel(group.pinLevel);
      setOrganizationId(group.organizationId);
      setOrganizationName(group.organizationName);
      setGroupName(group.groups[0].groupName);
      setGroupId(group.groups[0].groupId);
      setOpenGroupDropdown(false);
      setNewTargetFilter("");
    }
  };

  const clearInput = () => {
    setSelectedPlugee("");
    setSelectedTarget("");
    setNewFilter("");
    setNewTargetFilter("");
    setOrgIboNumber("");
    setOrgName("");
    setGroupName("");
    setIboNumber("");
    setOrganizationName("");
    setSyncChecked(false);
  };

  // // Error checking
  const validate = () => {
    let tempErrors = {};

    if (orgName === "") {
      tempErrors = {
        ...tempErrors,
        orgName: "Please select a name or IBO number.",
      };
    }

    if (groupName === "") {
      tempErrors = {
        ...tempErrors,
        groupName: "Please enter a group name.",
      };
    }

    if (orgIboNumber === iboNumber) {
      tempErrors = {
        ...tempErrors,
        groupName: "Cannot plug IBO into the same IBO.",
      };
    }

    const matchedIboData = data?.filter(
      (data) => data.iboNumber === Number(orgIboNumber)
    );

    if (matchedIboData && matchedIboData.length > 0) {
      const existingPlugin = matchedIboData.some((data) =>
        data.meta.some((plugin) =>
          plugin.groups.some((group) => group.groupId === groupId)
        )
      );
      if (existingPlugin) {
        tempErrors = {
          ...tempErrors,
          groupName: "This group already exists. Add a different group.",
        };
      }
    }

    setErrors(tempErrors);
    const hasErrors = Object.keys(tempErrors).length > 0;

    if (hasErrors) {
      return false;
    } else {
      return true;
    }
  };

  // // Submit the form or create
  const handleCreate = () => {
    let isValid = validate();

    if (isValid) {
      const data = {
        plugee: {
          name: name,
          iboNumber: iboNumber,
          pinLevel: pinLevel,
          organizationId: organizationId,
          organizationName: organizationName,

          groups: [
            {
              groupId: groupId,
              groupName: groupName,
              isAdmin: false,
            },
          ],
        },

        pluggedIntoMeta: {
          iboNumber: orgIboNumber,
          pinLevel: orgPinLevel,
          organizationId: orgOrganizationId,
        },

        sync: syncChecked,
      };
      onClick(data);
      setIsLoading(!isLoading);
    }
  };

  return (
    <div className={styles.container}>
      <Modal
        isOpen={isOpen}
        title="ADD META PLUGIN"
        closeBtn="none"
        className={styles.modal}
        onClose={onClose}
        children={
          <>
            <div className={styles.description}>
              Plug a whole group into another group (if user is in x, add to
              group y).
            </div>

            <div className={styles.inputContainer}>
              <div className={styles.inputItem}>
                <div className={styles.label}>
                  If an IBO is in the following group:
                </div>

                <div className={styles.dropDownContainer}>
                  <div
                    className={styles.selected}
                    onClick={handleDropdownClick}
                  >
                    <input
                      type="text"
                      ref={ref}
                      placeholder={
                        selectedPlugee.length > 0
                          ? selectedPlugee.join(" > ")
                          : "Search by Organization, Group, or IBO #"
                      }
                      value={displayValue(true)}
                      onChange={(e) => {
                        setNewFilter(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {!loading && openDropDown && (
                  <div className={styles.selectOptions}>
                    <>
                      {filteredData(sortedData, newFilter).map((item, _id) => (
                        <div
                          key={item._id}
                          className={styles.selectOption}
                          onClick={() => {
                            groupSearchSelect(item, true);
                            setParentOrganization(item);
                            setOrgName(item.organizationName);
                            setOrgIboNumber(item.iboNumber);
                            setOrgPinLevel(item.pinLevel);
                            setOrgOrganizationId(item.organizationId);
                            setErrors({ orgName: "", groupName: "" });
                          }}
                        >
                          <span className={styles.selectOrgName}>
                            {item.iboNumber} &gt; {item.organizationName} &gt;{" "}
                            {item.groups[0].groupName}
                          </span>
                        </div>
                      ))}
                    </>
                  </div>
                )}

                <span className={styles.errorOrgMsg}>{errors.orgName}</span>
              </div>

              <div className={styles.inputItem}>
                <div className={styles.label}> Add them to:</div>

                <div className={styles.dropDownContainer}>
                  <div
                    className={styles.selected}
                    onClick={() => setOpenGroupDropdown(!openGroupDropDown)}
                  >
                    <input
                      type="text"
                      placeholder={
                        selectedTarget.length > 0
                          ? selectedTarget.join(" > ")
                          : "Search by Organization, Group, or IBO #"
                      }
                      value={displayValue(false)}
                      onChange={(e) => {
                        setNewTargetFilter(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {!loading && openGroupDropDown && (
                  <div className={styles.groupOptions}>
                    {parentOrganization ? (
                      <>
                        {filteredTargetData(orgExist, newTargetFilter).map(
                          (group, index) => (
                            <div
                              key={index}
                              className={styles.groupOption}
                              onClick={() => {
                                groupSearchSelect(group, false);
                              }}
                            >
                              <span className={styles.selectOrgName}>
                                {group.iboNumber} &gt; {group.organizationName}{" "}
                                &gt; {group.groups[0].groupName}
                              </span>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <div className={styles.enterText}>
                        Select Group to see results
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span className={styles.errorGroupMsg}>{errors.groupName}</span>

              <div>
                <div
                  className={classNames(
                    styles.checkboxContainer,
                    syncChecked && styles.checked
                  )}
                  onClick={() => setSyncChecked(!syncChecked)}
                >
                  <div
                    className={classNames(
                      styles.icon,
                      !syncChecked && styles.hidden
                    )}
                  >
                    <Ok />
                  </div>
                </div>
                <div className={styles.checkboxLabel}>Sync</div>
              </div>
            </div>

            <ModalActionBar
              children={
                <div className={styles.buttonBar}>
                  <button type="button" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="button" onClick={() => handleCreate()}>
                    Add
                  </button>
                </div>
              }
            />
          </>
        }
      />
    </div>
  );
}

AddMetaPluginModal.propTypes = {
  /**
   * Boolean to determine if modal is open
   */
  isOpen: PropTypes.bool,
  /**
   * Callback function for when the modal is closed
   */
  onClose: PropTypes.func,
  /**
   * Function called when the add btn is clicked
   */
  onClick: PropTypes.func,
};
